import './DashboardCard.css';
import SunIcon from '../../../assets/icons/sun.svg?react';
import HumidIcon from '../../../assets/icons/humid.svg?react';
import WindIcon from '../../../assets/icons/wind.svg?react';
import CloudIcon from '../../../assets/icons/cloud.svg?react';
import RainyIcon from '../../../assets/icons/rainy.svg?react';
import SnowyIcon from '../../../assets/icons/snowy.svg?react';
import MoonIcon from '../../../assets/icons/moon.svg?react';
import MapIcon from '../../../assets/icons/map.svg?react';
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg?react';
import { SkeletonCircle, SkeletonText, SkeletonRectangle } from '../../../components/Skeleton/Skeleton';
import { useMemo, useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import jejuRegionData from '../../../assets/jeju_region_en.json';
import { useWeather, useWeatherToday } from '../../../hooks/queries/weatherQueries';
import { STORAGE_KEYS } from '../../../utils/constants';

const WeatherCard = ({ colSpan = 1, rowSpan = 1, dashboardData = {} }) => {
  // 지역 선택 상태 (localStorage에서 복원, 없거나 유효하지 않으면 기본값: 제주시 건입동)
  const [selectedRegion, setSelectedRegion] = useState(() => {
    try {
      const savedRegion = localStorage.getItem(STORAGE_KEYS.WEATHER_CARD_SELECTED_REGION);
      if (savedRegion) {
        const parsed = JSON.parse(savedRegion);
        // 저장된 값이 유효한지 확인 (sigungu와 eupmyeondong이 모두 있어야 함)
        if (parsed.sigungu && parsed.eupmyeondong) {
          return {
            sido: parsed.sido || '제주특별자치도',
            sigungu: parsed.sigungu || '제주시',
            eupmyeondong: parsed.eupmyeondong || '건입동'
          };
        }
      }
      // localStorage에 값이 없거나 유효하지 않으면 기본값 사용 (저장은 useEffect에서 처리)
      return {
        sido: '제주특별자치도',
        sigungu: '제주시',
        eupmyeondong: '건입동'
      };
    } catch (error) {
      console.warn('Failed to parse saved region from localStorage:', error);
      // 에러 발생 시 기본값 사용 (저장은 useEffect에서 처리)
      return {
        sido: '제주특별자치도',
        sigungu: '제주시',
        eupmyeondong: '건입동'
      };
    }
  });

  const [isSigunguOpen, setIsSigunguOpen] = useState(false);
  const [isEupmyeondongOpen, setIsEupmyeondongOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 시군구 목록
  const sigunguList = useMemo(() => {
    return [...new Set(jejuRegionData.map(item => item.RegionSigungu))];
  }, []);

  // 읍면동 목록 (시군구 선택 시 필터링)
  const eupmyeondongList = useMemo(() => {
    if (!selectedRegion.sigungu) return [];
    return [...new Set(
      jejuRegionData
        .filter(item => item.RegionSigungu === selectedRegion.sigungu)
        .map(item => item.RegionEupMyuonDong)
    )];
  }, [selectedRegion.sigungu]);

  // regionCode 계산
  const regionCode = useMemo(() => {
    if (!selectedRegion.eupmyeondong) return null;
    
    const regionData = jejuRegionData.find(data => 
      data.RegionSigungu === selectedRegion.sigungu && 
      data.RegionEupMyuonDong === selectedRegion.eupmyeondong
    );
    
    return regionData?.RegionCode ? regionData.RegionCode.toString().slice(0, -2) : null;
  }, [selectedRegion]);

  // 날씨 데이터 조회
  const targetDateStr = dayjs().format('YYYY-MM-DD');
  const { data: weatherForecastData, isLoading: weatherForecastLoading } = useWeather(regionCode, {
    enabled: !!regionCode
  });
  const { data: weatherTodayData, isLoading: weatherTodayLoading } = useWeatherToday(regionCode, targetDateStr, {
    enabled: !!regionCode
  });

  const [nowWeather, setNowWeather] = useState(null);
  const [isNight, setIsNight] = useState(false);

  // 현재 날씨 데이터 설정
  useEffect(() => {
    if (weatherTodayData) {
      const currentHour = dayjs().format('HH');
      const currentWeather = weatherTodayData.Items?.find(item => dayjs(item.WeatherDate).format('HH') === currentHour);
      setNowWeather(currentWeather);
      // 18시 ~ 06시 사이는 밤
      if (parseInt(currentHour) >= 18 || parseInt(currentHour) <= 6) {
        setIsNight(true);
      } else {
        setIsNight(false);
      }
    }
  }, [weatherTodayData]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSigunguOpen(false);
        setIsEupmyeondongOpen(false);
      }
    };

    if (isSigunguOpen || isEupmyeondongOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSigunguOpen, isEupmyeondongOpen]);

  // 지역 선택 상태가 변경될 때마다 localStorage에 저장 (유효한 값일 때만)
  useEffect(() => {
    // sigungu와 eupmyeondong이 모두 있을 때만 저장
    if (selectedRegion.sigungu && selectedRegion.eupmyeondong) {
      try {
        localStorage.setItem(STORAGE_KEYS.WEATHER_CARD_SELECTED_REGION, JSON.stringify(selectedRegion));
      } catch (error) {
        console.warn('Failed to save region to localStorage:', error);
      }
    }
  }, [selectedRegion]);

  // 시군구 선택 핸들러
  const handleSigunguSelect = (sigungu) => {
    setSelectedRegion({
      sido: '제주특별자치도',
      sigungu: sigungu,
      eupmyeondong: ''
    });
    setIsSigunguOpen(false);
    if (sigungu) {
      setIsEupmyeondongOpen(true);
    } else {
      setIsEupmyeondongOpen(false);
    }
  };

  // 읍면동 선택 핸들러
  const handleEupmyeondongSelect = (eupmyeondong) => {
    setSelectedRegion(prev => ({
      ...prev,
      eupmyeondong: eupmyeondong
    }));
    setIsEupmyeondongOpen(false);
  };

  // 지역 선택 결과를 문자열로 변환
  const regionString = useMemo(() => {
    if (!selectedRegion.eupmyeondong) {
      return '지역을 선택해주세요';
    }
    return `${selectedRegion.sigungu} ${selectedRegion.eupmyeondong}`;
  }, [selectedRegion]);

  const getWeatherIcon = (sky, pty, isNight, size = 74) => {
    if (pty === '없음') {
      if (sky === '맑음') {
        if (isNight) {
          return <MoonIcon color='var(--purple-500)' width={size} height={size}/>;
        }
        return <SunIcon color='var(--yellow-500)' width={size} height={size}/>;
      } else if (sky === '구름많음') {
        return <CloudIcon color='var(--blue-500)' width={size} height={size}/>;
      }
      else if (sky === '흐림') {
        return <CloudIcon color='var(--blue-500)' width={size} height={size}/>;
      }
    }
    else {
      if(pty === '비' || pty === '빗방울') {
        return <RainyIcon color='var(--blue-500)' width={size} height={size}/>;
      }
      else if(pty === '눈' || pty === '눈날림') {
        return <SnowyIcon color='var(--blue-500)' width={size} height={size}/>;
      }
      else if(pty === '비/눈' || pty === '빗방울눈날림') {
        return <RainyIcon color='var(--blue-500)' width={size} height={size}/>;
      }
    }
  };

  const isLoading = weatherForecastLoading || weatherTodayLoading;
  const hasData = !!regionCode && !isLoading;

  return (
    <div 
      className="dashboard-card flex flex-col justify-between" 
    >
      {/* 지역 선택 헤더 */}
      <div className='flex align-center gap-5 mb-10' ref={dropdownRef} style={{ position: 'relative' }}>
        <MapIcon color='var(--gray-700)'/>
        <div className='flex align-center gap-5 flex-1' style={{ position: 'relative' }}>
          {/* 시군구 선택 */}
          <div style={{ position: 'relative', flex: '1' }}>
            <div
              className='weather-region-selector'
              onClick={() => {
                setIsSigunguOpen(!isSigunguOpen);
                if (isSigunguOpen) setIsEupmyeondongOpen(false);
              }}
            >
              <span className={selectedRegion.sigungu ? '' : 'placeholder'}>
                {selectedRegion.sigungu || '시군구'}
              </span>
              <ChevronDownIcon className={isSigunguOpen ? 'up' : ''} />
            </div>
            {isSigunguOpen && (
              <div className='weather-region-dropdown'>
                {sigunguList.map((sigungu) => (
                  <div
                    key={sigungu}
                    className={`weather-region-option ${selectedRegion.sigungu === sigungu ? 'selected' : ''}`}
                    onClick={() => handleSigunguSelect(sigungu)}
                  >
                    {sigungu}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 읍면동 선택 */}
          {selectedRegion.sigungu && (
            <div style={{ position: 'relative', flex: '1' }}>
              <div
                className='weather-region-selector'
                onClick={() => {
                  if (selectedRegion.sigungu) {
                    setIsEupmyeondongOpen(!isEupmyeondongOpen);
                    if (isEupmyeondongOpen) setIsSigunguOpen(false);
                  }
                }}
              >
                <span className={selectedRegion.eupmyeondong ? '' : 'placeholder'}>
                  {selectedRegion.eupmyeondong || '동/읍/면'}
                </span>
                <ChevronDownIcon className={isEupmyeondongOpen ? 'up' : ''} />
              </div>
              {isEupmyeondongOpen && (
                <div className='weather-region-dropdown'>
                  {eupmyeondongList.map((eupmyeondong) => (
                    <div
                      key={eupmyeondong}
                      className={`weather-region-option ${selectedRegion.eupmyeondong === eupmyeondong ? 'selected' : ''}`}
                      onClick={() => handleEupmyeondongSelect(eupmyeondong)}
                    >
                      {eupmyeondong}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 날씨 정보 */}
      {hasData && !isLoading ? (
        <div className='flex flex-col gap-5 flex-1'>
          <div className='flex align-center gap-14 my-5'>
            {getWeatherIcon(nowWeather?.SkyCondition, nowWeather?.PrecipitationType, isNight, 64)}
            <div className='flex flex-col gap-2'>
              <p className='text-b2-400 text-gray-600'>{dayjs(nowWeather?.WeatherDate).format('HH')}시</p>
              <p className='text-h2 text-base'>{nowWeather?.Temperature != null ? nowWeather?.Temperature : '-'}°C</p>
              <div className='flex gap-5 align-center'>
                <div className='flex gap-2 align-center'>
                  <WindIcon color='var(--gray-600)'/>
                  <p className='text-b2-400 text-gray-600'>{nowWeather?.WindSpeed != null ? nowWeather?.WindSpeed : '-'}m/s</p>
                </div>
                <div className='flex gap-2 align-center'>
                  <HumidIcon color='var(--gray-600)'/>
                  <p className='text-b2-400 text-gray-600'>{nowWeather?.Humidity != null ? nowWeather?.Humidity : '-'}%</p>
                </div>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-10'>
            {weatherForecastData?.Items?.map((item, index) => (
              <div key={index} className='dashboard-weather-forecast-item'>
                {getWeatherIcon(item.SkyCondition, item.PrecipitationType, false, 24)}
                <div className='text-b1-700 text-gray-700'>{item.Temperature != null ? item.Temperature : '-'}°C</div>
                <div className='flex-1 text-b2-400 text-gray-600'>{item.SkyCondition}</div>
                <div className='text-b2-400 text-gray-600'>{dayjs(nowWeather?.WeatherDate).add(index+1, 'hour').format('HH')}시</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-5 flex-1'>
          <div className='flex align-center gap-14 my-5'>
            <SkeletonCircle size="60px" />
            <div className='flex flex-col gap-2'>
              <SkeletonText width="20px" height="16px" />
              <SkeletonText width="40px" height="32px" />
              <div className='flex gap-5 align-center'>
                <div className='flex gap-2 align-center'>
                  <SkeletonText width="50px" height="16px" />
                </div>
                <div className='flex gap-2 align-center'>
                  <SkeletonText width="50px" height="16px" />
                </div>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-10'>
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className='dashboard-weather-forecast-item flex align-center gap-2'>
                <SkeletonCircle size="20px" />
                <SkeletonText width="20px" height="16px" />
                <SkeletonText width="60px" height="16px" />
                <SkeletonText width="20px" height="16px" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherCard;
